'use client';

import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { motion, type Variants, type TargetAndTransition } from 'framer-motion';
import { Home, ArrowRight, Map } from 'lucide-react';
import { useLocale } from '@/i18n/context';

export default function NotFoundPage() {
  const router = useRouter();
  const { t, isRtl } = useLocale();

  // انیمیشن‌های کانتینر اصلی
  const containerVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6, 
        ease: "easeOut",
        staggerChildren: 0.2
      }
    }
  };

  // انیمیشن‌های فرزندان (آیتم‌های داخل کانتینر)
  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  // افکت شناور برای عدد 404 و آیکون
  const floatingAnimation: TargetAndTransition = {
    y: [-10, 10, -10],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col justify-center items-center px-4 sm:px-6 lg:px-8 overflow-hidden bg-[#F5F0E8] dark:bg-zinc-950 selection:bg-[#206A4F]/20 dark:selection:bg-emerald-500/30 text-center transition-colors duration-300" dir={isRtl ? "rtl" : "ltr"}>
      
      {/* مش‌گرادینت پس‌زمینه */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-[#206A4F]/10 dark:bg-emerald-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-[#206A4F]/10 dark:bg-emerald-500/10 rounded-full blur-[120px]" />
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 w-full max-w-2xl bg-white/40 dark:bg-zinc-900/60 backdrop-blur-3xl p-10 md:p-16 rounded-[3rem] shadow-[0_8px_40px_rgb(0,0,0,0.03)] border border-white/60 dark:border-zinc-800 flex flex-col items-center"
      >
        
        {/* المان گرافیکی 404 */}
        <motion.div variants={itemVariants} className="relative mb-8">
          <motion.div 
            animate={floatingAnimation}
            className="flex items-center justify-center gap-4 text-[#206A4F] dark:text-emerald-400"
          >
            <span className="text-8xl md:text-9xl font-black tracking-tighter opacity-90 drop-shadow-sm">4</span>
            <div className="w-20 h-20 md:w-28 md:h-28 bg-[#206A4F]/10 dark:bg-emerald-500/20 rounded-full flex items-center justify-center shadow-inner border border-[#206A4F]/10 dark:border-emerald-500/30 relative overflow-hidden">
              <Map size={48} className="text-[#206A4F] dark:text-emerald-400 opacity-80" strokeWidth={1.5} />
            </div>
            <span className="text-8xl md:text-9xl font-black tracking-tighter opacity-90 drop-shadow-sm">4</span>
          </motion.div>
        </motion.div>

        {/* متون پیام خطا */}
        <motion.div variants={itemVariants} className="mb-10">
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#206A4F] dark:text-white mb-4">
            {t.notFound.subtitle || t.notFound.title}
          </h1>
          <p className="text-[#206A4F]/70 dark:text-zinc-400 text-lg md:text-xl font-medium max-w-md mx-auto leading-relaxed">
            {t.notFound.desc}
          </p>
        </motion.div>

        {/* دکمه‌های ناوبری */}
        <motion.div 
          variants={itemVariants}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto"
        >
          <Link href="/" passHref className="w-full sm:w-auto">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-[#206A4F] dark:bg-emerald-500 text-[#F5F0E8] dark:text-black px-8 py-3.5 rounded-2xl font-black shadow-lg shadow-[#206A4F]/20 dark:shadow-none hover:shadow-xl hover:bg-[#18533E] dark:hover:bg-emerald-400 transition-all cursor-pointer"
            >
              <Home size={20} />
              <span>{t.notFound.homeBtn}</span>
            </motion.button>
          </Link>

          <motion.button
            onClick={() => router.back()}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-white dark:bg-zinc-800 text-[#206A4F] dark:text-emerald-300 px-8 py-3.5 rounded-2xl font-extrabold border border-[#206A4F]/10 dark:border-zinc-700 hover:bg-[#206A4F]/5 dark:hover:bg-zinc-700/80 shadow-sm transition-all cursor-pointer"
          >
            <ArrowRight size={20} className="rtl:rotate-180" />
            <span>{t.common.back}</span>
          </motion.button>
        </motion.div>

      </motion.div>
      
    </div>
  );
}